import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChevronLeft, Clock, Eye, Heart, User, Calendar, Tag, BookOpen,
} from "lucide-react";

const DIFFICULTY_COLOR = {
  Principiante: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  Intermedio: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  Avanzado: "bg-rose-500/10 text-rose-400 border-rose-500/20",
};

export default function KBArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    api.get(`/kb/articles/${slug}`).then(({ data }) => setArticle(data));
  }, [slug]);

  const handleFavorite = async () => {
    if (!article || favorited) return;
    await api.post(`/kb/articles/${article.id}/favorite`);
    setFavorited(true);
    setArticle((a) => ({ ...a, favorites: (a.favorites || 0) + 1 }));
  };

  if (!article) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("es-CL", {
        day: "2-digit", month: "short", year: "numeric",
      });
    } catch { return ""; }
  };

  return (
    <div className="max-w-4xl mx-auto animate-in-up">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-6">
        <Link to="/">
          <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground hover:text-foreground">
            <ChevronLeft className="h-4 w-4" /> Knowledge Hub
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8">
        {/* Main content */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-4 tracking-tight">
            {article.title}
          </h1>

          {article.summary && (
            <p className="text-base text-muted-foreground mb-6 leading-relaxed">
              {article.summary}
            </p>
          )}

          {/* Article body */}
          <Card className="border-border/50 bg-card">
            <CardContent className="p-6 md:p-8 prose prose-invert prose-sm max-w-none
              prose-headings:text-foreground prose-headings:font-semibold
              prose-p:text-muted-foreground prose-p:leading-relaxed
              prose-a:text-accent prose-a:no-underline hover:prose-a:underline
              prose-strong:text-foreground
              prose-code:text-accent prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
              prose-pre:bg-muted prose-pre:border prose-pre:border-border/50
              prose-li:text-muted-foreground
              prose-blockquote:border-accent/50 prose-blockquote:text-muted-foreground">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {article.content || "*Sin contenido disponible aún.*"}
              </ReactMarkdown>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar metadata */}
        <aside className="space-y-4">
          {/* Meta card */}
          <Card className="border-border/50 bg-card sticky top-24">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                {article.difficulty && (
                  <Badge variant="outline" className={DIFFICULTY_COLOR[article.difficulty]}>
                    {article.difficulty}
                  </Badge>
                )}
                <Badge variant="outline" className="text-[10px] bg-muted/50 text-muted-foreground">
                  v{article.version}
                </Badge>
              </div>

              <div className="space-y-3 text-xs text-muted-foreground">
                {article.author && (
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5" />
                    <span>{article.author}</span>
                  </div>
                )}
                {article.estimated_minutes && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{article.estimated_minutes} min de lectura</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Eye className="h-3.5 w-3.5" />
                  <span>{article.views} vistas</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>{fmtDate(article.updated_at || article.created_at)}</span>
                </div>
              </div>

              {/* Tags */}
              {article.tags?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2 text-xs text-muted-foreground">
                    <Tag className="h-3 w-3" /> Etiquetas
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {article.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-[10px] bg-muted/30 border-border/50">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Favorite button */}
              <Button
                variant="outline"
                size="sm"
                className={`w-full gap-2 ${favorited ? "text-rose-400 border-rose-400/30" : ""}`}
                onClick={handleFavorite}
                disabled={favorited}
              >
                <Heart className={`h-3.5 w-3.5 ${favorited ? "fill-current" : ""}`} />
                {favorited ? "Guardado" : "Guardar"} ({article.favorites})
              </Button>
            </CardContent>
          </Card>

          {/* Related articles */}
          {article.related_articles?.length > 0 && (
            <Card className="border-border/50 bg-card">
              <CardContent className="p-5">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <BookOpen className="h-3 w-3" /> Relacionados
                </h4>
                <div className="space-y-2">
                  {article.related_articles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/kb/article/${rel.slug}`}
                      className="block rounded-lg p-2.5 hover:bg-muted/50 transition-colors group"
                    >
                      <p className="text-xs font-medium text-foreground group-hover:text-accent transition-colors line-clamp-2">
                        {rel.title}
                      </p>
                      {rel.estimated_minutes && (
                        <span className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                          <Clock className="h-2.5 w-2.5" /> {rel.estimated_minutes} min
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}
